import { useEffect, useId, useState } from "react";
import { useParams } from "react-router-dom";
import { useLocale, useTranslations } from "next-intl";
import { IconAlertTriangle } from "@tabler/icons-react";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { type AiPendingAction } from "@/providers/AiAssistantProvider";
import type { AiClientConfirmation } from "@/lib/ai/ai-tool-bridge";
import {
  IMPORT_LEGAL_BASIS_TEXT_KEY,
  importLegalBasisConfirmation,
} from "@/lib/legal/importLegalBasis";
import { fetchGuestDeleteImpact } from "@/lib/api/handlers/GuestHandling";
import { trashOf } from "@/lib/guests/trashCopy";

/**
 * W6 guest trash: the ids a `delete_guests` call names, when its input carries
 * them. The card asks the delete impact for those ids so it can ask "move N
 * guests to the trash?" instead of "cannot be undone" — the AI session may
 * never delete permanently (403 `permanentDeleteNotAllowedForAi`), so the
 * card offers no permanent lane either way. A filter-shaped input is not
 * probed: the card keeps today's irreversible line, the honest default.
 */
export function deleteGuestIdsOf(action: AiPendingAction): string[] | null {
  if (action.toolName !== "delete_guests") return null;
  try {
    const input = JSON.parse(action.toolInputJson) as { guestIds?: unknown; ids?: unknown };
    const raw = Array.isArray(input.guestIds) ? input.guestIds : Array.isArray(input.ids) ? input.ids : null;
    if (!raw) return null;
    const ids = raw.filter((v): v is string => typeof v === "string" && v.length > 0);
    return ids.length > 0 ? ids : null;
  } catch {
    return null;
  }
}

/**
 * Non-null only when the backend SAID this delete lands in the trash; `count`
 * is the number of guest rows the delete really moves — the named guests PLUS
 * their cascaded companions (`totalToDelete`): unlike the bulk dialog the card
 * has no impact box to list the cascade, so the question carries the whole
 * number. Ids naming no guest at all keep today's irreversible line, the
 * honest default for a delete the card cannot size.
 */
function useDeleteGuestsTrash(action: AiPendingAction, eventId: string | undefined): { count: number } | null {
  const [trash, setTrash] = useState<{ count: number } | null>(null);
  const ids = deleteGuestIdsOf(action);
  const idsKey = ids ? ids.join(",") : "";
  useEffect(() => {
    setTrash(null);
    if (!eventId || !ids) return;
    let alive = true;
    void fetchGuestDeleteImpact(eventId, { mode: "ids", ids }).then((impact) => {
      if (!alive) return;
      const selected = impact && Number.isFinite(impact.selected) ? impact.selected : 0;
      const total = impact && Number.isFinite(impact.totalToDelete) ? impact.totalToDelete : selected;
      const count = impact && trashOf(impact) ? Math.max(total, selected) : 0;
      setTrash(count > 0 ? { count } : null);
    });
    return () => {
      alive = false;
    };
    // `ids` is rebuilt per render from the same JSON; `idsKey` is its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, idsKey, action.toolUseId]);
  return trash;
}

/**
 * Welle Z W8: the client tool that imports a file of people. Its card carries
 * the same legal-basis sentence as the import wizard, as a box the organiser
 * must tick before Approve works.
 */
const LEGAL_BASIS_TOOL = "import_guests_from_spreadsheet";

/**
 * Tools whose effect cannot be undone — deletions, cancellations, revocations
 * and one-way sends. These get the red "danger" treatment + an irreversibility
 * note. Anything else gets the neutral confirmation styling.
 *
 * Matched by prefix (delete_, remove_, cancel_, revoke_) so new tools are
 * covered automatically; a few non-prefixed one-way actions are listed below.
 */
const DESTRUCTIVE_PREFIXES = ["delete_", "remove_", "cancel_", "revoke_"];
const DESTRUCTIVE_TOOLS = new Set([
  "delete_guests",
  "send_email_campaign",
  "update_guests_by_filter",
  "noshow_appointment_guest",
  // Frees every occupied chair of one table and mails every guest it unseats.
  // No prefix covers it — `clear_` is not in the list, and adding the prefix
  // would sweep in future read-only "clear filter"-shaped tools.
  "clear_seating_furniture_seats",
]);

/**
 * Exported for its own test. The card's red border and its "cannot be undone"
 * line are the only warning a user gets before approving a tool call, and the
 * classifier that decides them was a prefix list plus four hand-written names —
 * a new destructive tool joins neither by itself.
 */
export function isDestructive(action: AiPendingAction): boolean {
  const toolName = action.toolName;
  if (!toolName) return false;
  if (DESTRUCTIVE_TOOLS.has(toolName)) return true;
  // Import is only destructive in overwrite mode (create is additive).
  if (toolName === "import_guests_from_spreadsheet") return isOverwriteImport(action);
  return DESTRUCTIVE_PREFIXES.some((p) => toolName.startsWith(p));
}

function isOverwriteImport(action: AiPendingAction): boolean {
  if (action.toolName !== "import_guests_from_spreadsheet") return false;
  try {
    return (JSON.parse(action.toolInputJson) as { mode?: string }).mode === "overwrite";
  } catch {
    return false;
  }
}

/** Human label for a tool — translated when we have one, raw name otherwise. */
function useToolLabel() {
  const t = useTranslations("aiAssistant");
  return (name: string | undefined) =>
    name && t.has(`toolNames.${name}`) ? t(`toolNames.${name}`) : (name ?? "");
}

/**
 * Approval card for a destructive tool call (plan §7.2 confirmation gate).
 *
 * For the spreadsheet import (W8) the card also asks for the legal-basis
 * confirmation. The tick is the card's own state: on Approve the card builds
 * the confirmation object from its constants and the UI locale and passes it
 * as onResolve's second argument — nothing of it comes from the tool input
 * the model wrote. Mounted by AiChatPanel and WizardVeloPanel, both of which
 * pass onResolve straight to confirmPendingTool. Decline never carries it.
 */
export function ConfirmationCard({
  action,
  onResolve,
}: {
  action: AiPendingAction;
  onResolve: (approved: boolean, confirmation?: AiClientConfirmation) => void;
}) {
  const t = useTranslations("aiAssistant");
  const tRoot = useTranslations();
  const locale = useLocale();
  const toolLabel = useToolLabel();
  const { eventId } = useParams<{ eventId: string }>();
  // W6: non-null only when the backend SAID this delete lands in the trash.
  const trash = useDeleteGuestsTrash(action, eventId);
  const danger = isDestructive(action);
  const overwriteImport = isOverwriteImport(action);
  const asksLegalBasis = action.toolName === LEGAL_BASIS_TOOL;
  // Per card instance; the panels mount a fresh card per pending action
  // (keyed by toolUseId), so a new import never inherits an old tick.
  const [legalBasisTicked, setLegalBasisTicked] = useState(false);
  const approveBlocked = asksLegalBasis && !legalBasisTicked;
  // A blocked Approve stays focusable and is described by the sentence, so a
  // screen reader hears WHY it is off (audit-z L-2).
  const legalBasisLabelId = useId();

  return (
    <div
      data-testid="ai-tool-confirmation"
      data-danger={danger ? "true" : undefined}
      className={`rounded-2xl border surface-secondary px-3.5 py-3 text-sm flex flex-col gap-2 ${
        danger ? "outline-error" : "outline-quaternary"
      }`}
    >
      <div
        className={`flex items-center gap-1.5 font-medium ${danger ? "font-error" : ""}`}
      >
        <IconAlertTriangle size={16} className="shrink-0" />
        {t("confirmTitle")}
      </div>
      <div>{t("confirmBody", { tool: toolLabel(action.toolName) })}</div>
      {overwriteImport && <div className="font-error">{t("confirmOverwriteImport")}</div>}
      {danger && trash ? (
        // The same plain question the bulk dialog asks — the retention is not
        // spelled out (owner, 22.10.).
        <div data-testid="ai-tool-confirmation-trash">{tRoot("bulkConfirm.trashQuestion", { count: trash.count })}</div>
      ) : danger ? (
        <div className="font-error">{t("confirmIrreversible")}</div>
      ) : null}
      {asksLegalBasis && (
        <CheckboxWithValidation
          isChecked={legalBasisTicked}
          ariaRequired
          alignStart
          size={18}
          onCheckChangeAction={setLegalBasisTicked}
          testId="ai-tool-confirmation-legal-basis"
        >
          <span id={legalBasisLabelId} className="text-sm">
            {tRoot(IMPORT_LEGAL_BASIS_TEXT_KEY)}
          </span>
        </CheckboxWithValidation>
      )}
      <div className="flex gap-2">
        <PrimaryButton
          text={t("confirmApprove")}
          size="small"
          paddingOverride="px-3 py-1.5"
          rounded="rounded-xl"
          width="flex-1"
          color={danger ? "surface-error-dark" : undefined}
          disabled={approveBlocked}
          focusableWhenDisabled={approveBlocked}
          ariaDescribedBy={approveBlocked ? legalBasisLabelId : undefined}
          testId="ai-tool-confirmation-approve"
          onClick={() => {
            if (!asksLegalBasis) {
              onResolve(true);
              return;
            }
            // Belt and braces: the button is disabled without the tick.
            if (!legalBasisTicked) return;
            onResolve(true, importLegalBasisConfirmation(locale));
          }}
        />
        <SecondaryButton
          text={t("confirmDecline")}
          size="small"
          customPadding="px-3 py-1.5"
          rounded="rounded-xl"
          borderColor="outline-quaternary"
          width="flex-1"
          testId="ai-tool-confirmation-decline"
          onClick={() => onResolve(false)}
        />
      </div>
    </div>
  );
}
